import { useEffect, useState, useCallback, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LiveMap } from "@/components/LiveMap";
import { fmtDateTime } from "@/lib/format";
import { MapPin, Phone, PackageCheck, Bike, XCircle, Navigation } from "lucide-react";
import { toast } from "sonner";

const NEXT = { assigned: { to: "picked_up", label: "Mark Picked up" }, picked_up: { to: "out_for_delivery", label: "Start delivery" } };
const LABEL = { pending: "Pending", assigned: "Assigned", picked_up: "Picked up", out_for_delivery: "Out for delivery", delivered: "Delivered", failed: "Failed" };
const ACTIVE = ["assigned", "picked_up", "out_for_delivery"];
const FAIL_REASONS = ["Customer not available", "Wrong / incomplete address", "Customer refused delivery", "Customer unreachable by phone", "Payment not ready", "Damaged in transit", "Other"];
const RELATIONSHIPS = ["Self", "Family member", "Neighbour", "Colleague", "Security / concierge", "Other"];

const navUrl = (o) =>
  o.dest_lat != null
    ? `https://www.google.com/maps/dir/?api=1&destination=${o.dest_lat},${o.dest_lng}`
    : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(o.address || "")}`;

export default function RiderView() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [route, setRoute] = useState({ stops: [], optimized: false, rider_location: null });
  const [pod, setPod] = useState(null);
  const [fail, setFail] = useState(null);

  const load = useCallback(() => {
    if (!user?.id) return;
    api.get("/deliveries", { params: { rider_id: user.id } })
      .then((r) => setRows(r.data.filter((d) => ACTIVE.includes(d.status))))
      .catch(() => {});
    api.get("/rider/route").then((r) => setRoute(r.data)).catch(() => {});
  }, [user]);
  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, [load]);

  // Broadcast live GPS while the rider is on the road
  const lastSent = useRef(0);
  useEffect(() => {
    if (!navigator.geolocation) return;
    const id = navigator.geolocation.watchPosition((pos) => {
      const now = Date.now();
      if (now - lastSent.current < 15000) return;
      lastSent.current = now;
      api.post("/rider/location", { latitude: pos.coords.latitude, longitude: pos.coords.longitude }).then(load).catch(() => {});
    }, () => {}, { enableHighAccuracy: true, maximumAge: 10000, timeout: 20000 });
    return () => navigator.geolocation.clearWatch(id);
  }, [load]);

  const advance = async (d, status) => {
    try { await api.post(`/deliveries/${d.id}/status`, { status }); toast.success(`Marked ${LABEL[status]}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const openPod = (d) => {
    const base = { id: d.id, recipient_name: d.customer_name || "", note: "", relationship: "Self", photo: null, lat: "", lng: "" };
    setPod(base);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setPod((p) => (p && p.id === d.id ? { ...p, lat: String(pos.coords.latitude), lng: String(pos.coords.longitude) } : p)),
        () => {}, { enableHighAccuracy: true, timeout: 8000 }
      );
    }
  };
  const doPod = async () => {
    if (!pod.recipient_name.trim()) return toast.error("Enter who received the order");
    if (!pod.photo) return toast.error("Take a photo as proof of delivery");
    try {
      const fd = new FormData();
      fd.append("recipient_name", pod.recipient_name);
      fd.append("note", pod.note || "");
      fd.append("relationship", pod.relationship || "");
      fd.append("latitude", pod.lat || "");
      fd.append("longitude", pod.lng || "");
      if (pod.photo) fd.append("photo", pod.photo);
      await api.post(`/deliveries/${pod.id}/pod`, fd);
      toast.success("Delivery confirmed"); setPod(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const doFail = async () => {
    if (!fail.reason) return toast.error("Select a reason");
    try { await api.post(`/deliveries/${fail.id}/status`, { status: "failed", reason: fail.reason, note: fail.note }); toast.success("Delivery failed — items sent to quarantine for review"); setFail(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const seqMap = {};
  (route.stops || []).forEach((s) => { seqMap[s.id] = s; });
  const orderedRows = [...rows].sort((a, b) => (seqMap[a.id]?.seq || 99) - (seqMap[b.id]?.seq || 99));

  const markers = [];
  if (route.rider_location) markers.push({ lat: route.rider_location.latitude, lng: route.rider_location.longitude, color: "#2563eb", label: "You" });
  (route.stops || []).forEach((s) => { if (s.dest_lat != null) markers.push({ lat: s.dest_lat, lng: s.dest_lng, color: "#e11d48", label: `${s.seq}. ${s.customer_name}` }); });

  return (
    <div className="max-w-md mx-auto">
      <PageHeader title="My Route" subtitle={route.optimized ? "Optimised order for today's run" : "Your active stops today"} />
      {markers.length > 0 && <Card className="p-2 shadow-sm mb-4"><LiveMap markers={markers} height={240} /></Card>}
      {orderedRows.length === 0 && (
        <Card className="p-10 text-center text-muted-foreground shadow-sm"><Bike className="h-10 w-10 mx-auto mb-3 opacity-40" />No active deliveries right now</Card>
      )}
      <div className="space-y-4">
        {orderedRows.map((d) => {
          const s = seqMap[d.id] || {};
          const target = { ...d, dest_lat: s.dest_lat, dest_lng: s.dest_lng };
          return (
          <Card key={d.id} className="p-4 shadow-sm" data-testid={`rider-card-${d.invoice_no}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="flex items-center gap-2">
                <span className="h-6 w-6 rounded-full bg-sky-600 text-white text-xs font-bold flex items-center justify-center" data-testid={`rider-seq-${d.invoice_no}`}>{s.seq || "•"}</span>
                <span className="font-mono text-xs text-muted-foreground">{d.invoice_no}</span>
              </span>
              <StatusBadge value={d.status === "delivered" ? "received" : d.status === "assigned" ? "requested" : "dispatched"} label={LABEL[d.status]} />
            </div>
            <div className="font-semibold text-foreground text-lg leading-tight">{d.customer_name}</div>
            <div className="flex items-start gap-2 text-sm text-muted-foreground mt-1"><MapPin className="h-4 w-4 mt-0.5 shrink-0" />{d.address || "—"}</div>
            {d.scheduled_for && <div className="text-xs text-muted-foreground mt-1">Scheduled: {fmtDateTime(d.scheduled_for)}</div>}
            <div className="flex items-center gap-4 mt-2">
              <a href={navUrl(target)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm" style={{ color: "var(--mm-action)" }} data-testid={`rider-navigate-${d.invoice_no}`}><Navigation className="h-4 w-4" /> Navigate</a>
              {d.phone && <a href={`tel:${d.phone}`} className="inline-flex items-center gap-1.5 text-sm" style={{ color: "var(--mm-action)" }} data-testid={`rider-call-${d.invoice_no}`}><Phone className="h-4 w-4" /> Call</a>}
            </div>
            <div className="mt-4 grid gap-2">
              {NEXT[d.status] && (
                <Button className="h-14 text-base w-full" data-testid={`rider-advance-${d.invoice_no}`} onClick={() => advance(d, NEXT[d.status].to)}>{NEXT[d.status].label}</Button>
              )}
              {d.status === "out_for_delivery" && (
                <Button className="h-14 text-base w-full bg-emerald-600 hover:bg-emerald-700" data-testid={`rider-deliver-${d.invoice_no}`} onClick={() => openPod(d)}><PackageCheck className="h-5 w-5 mr-2" /> Mark Delivered</Button>
              )}
              <Button variant="outline" className="h-11 text-sm w-full text-rose-600 border-rose-300 hover:bg-rose-50" data-testid={`rider-fail-${d.invoice_no}`} onClick={() => setFail({ id: d.id, reason: "", note: "" })}><XCircle className="h-4 w-4 mr-2" /> Mark delivery failed</Button>
            </div>
          </Card>
          );
        })}
      </div>

      <Dialog open={!!pod} onOpenChange={(o) => !o && setPod(null)}>
        <DialogContent className="max-w-sm"><DialogHeader><DialogTitle>Proof of delivery</DialogTitle><DialogDescription>Capture who received the order and a handover photo.</DialogDescription></DialogHeader>
          {pod && <div className="space-y-3">
            <div><Label>Received by</Label><Input data-testid="rider-pod-recipient" value={pod.recipient_name} onChange={(e) => setPod((p) => ({ ...p, recipient_name: e.target.value }))} /></div>
            <div><Label>Relationship</Label>
              <Select value={pod.relationship} onValueChange={(v) => setPod((p) => ({ ...p, relationship: v }))}>
                <SelectTrigger data-testid="rider-pod-relationship"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{RELATIONSHIPS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Handover photo</Label>
              <Input type="file" accept="image/*" capture="environment" data-testid="rider-pod-photo" onChange={(e) => setPod((p) => ({ ...p, photo: e.target.files?.[0] || null }))} />
              {pod.photo && <div className="text-xs text-emerald-600 mt-1">✓ {pod.photo.name}</div>}
            </div>
            <div><Label>Note</Label><Input data-testid="rider-pod-note" value={pod.note} onChange={(e) => setPod((p) => ({ ...p, note: e.target.value }))} /></div>
            <div className="text-xs text-muted-foreground" data-testid="rider-pod-gps">{pod.lat && pod.lng ? `📍 Location captured (${Number(pod.lat).toFixed(4)}, ${Number(pod.lng).toFixed(4)})` : "📍 Capturing location…"}</div>
          </div>}
          <DialogFooter><Button variant="outline" onClick={() => setPod(null)}>Cancel</Button><Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="rider-pod-confirm" onClick={doPod}>Confirm delivered</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!fail} onOpenChange={(o) => !o && setFail(null)}>
        <DialogContent className="max-w-sm"><DialogHeader><DialogTitle>Mark delivery failed</DialogTitle><DialogDescription>Choose why the delivery could not be completed.</DialogDescription></DialogHeader>
          {fail && <div className="space-y-3">
            <div><Label>Reason</Label>
              <Select value={fail.reason} onValueChange={(v) => setFail((f) => ({ ...f, reason: v }))}>
                <SelectTrigger data-testid="rider-fail-reason"><SelectValue placeholder="Select a reason" /></SelectTrigger>
                <SelectContent>{FAIL_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Note (optional)</Label><Input data-testid="rider-fail-note" value={fail.note} onChange={(e) => setFail((f) => ({ ...f, note: e.target.value }))} /></div>
          </div>}
          <DialogFooter><Button variant="outline" onClick={() => setFail(null)}>Cancel</Button><Button className="bg-rose-600 hover:bg-rose-700" data-testid="rider-fail-confirm" onClick={doFail}>Confirm failed</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
